import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';
import DownloadRequestsPanel from '../components/DownloadRequestsPanel';
import { 
  Activity, Search, Filter, ChevronDown, User, 
  CreditCard, Users, FileText, Settings, Clock,
  AlertTriangle, CheckCircle, XCircle, Edit2
} from 'lucide-react';

const API_URL = getApiUrl();

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    fetchLogs();
    fetchUsers();
  }, [limit]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      // API returns array directly, not {users: [...]}
      const usersData = Array.isArray(response.data) ? response.data : (response.data.users || []);
      console.log('Fetched users:', usersData.length);
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback: Don't show user filter if we can't fetch users
    }
  };

  // Extract unique users from logs as fallback
  const getUniqueUsersFromLogs = () => {
    const userIds = [...new Set(logs.map(l => l.user_id).filter(Boolean))];
    return userIds.map(id => {
      const foundUser = users.find(u => u.user_id === id);
      return foundUser || { user_id: id, full_name: id?.slice(0, 20) + '...' };
    });
  };

  // Use fetched users if available, otherwise extract from logs
  const displayUsers = users.length > 0 ? users : getUniqueUsersFromLogs();

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/analytics/activity-logs?limit=${limit}`, { withCredentials: true });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    const icons = {
      create: <CheckCircle className="w-4 h-4 text-green-500" />,
      update: <Edit2 className="w-4 h-4 text-blue-500" />,
      delete: <XCircle className="w-4 h-4 text-red-500" />,
      role_change: <User className="w-4 h-4 text-purple-500" />,
      suspend: <AlertTriangle className="w-4 h-4 text-amber-500" />,
      activate: <CheckCircle className="w-4 h-4 text-green-500" />,
      create_payment_link: <CreditCard className="w-4 h-4 text-indigo-500" />,
      approve_commission: <CheckCircle className="w-4 h-4 text-green-500" />,
      reject_commission: <XCircle className="w-4 h-4 text-red-500" />,
      admin_impersonation: <User className="w-4 h-4 text-amber-500" />,
      invite_user: <Users className="w-4 h-4 text-blue-500" />,
    };
    return icons[actionType] || <Activity className="w-4 h-4 text-slate-400" />;
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      lead: <Users className="w-4 h-4 text-slate-400" />,
      user: <User className="w-4 h-4 text-slate-400" />,
      payment: <CreditCard className="w-4 h-4 text-slate-400" />,
      invoice: <FileText className="w-4 h-4 text-slate-400" />,
      doctor: <User className="w-4 h-4 text-slate-400" />,
      referral: <Users className="w-4 h-4 text-slate-400" />,
      settings: <Settings className="w-4 h-4 text-slate-400" />,
    };
    return icons[entityType] || <Activity className="w-4 h-4 text-slate-400" />;
  };

  const formatAction = (action) => {
    return action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.action_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.entity_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.entity_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = actionFilter === 'all' || log.action_type === actionFilter;
    const matchesUser = userFilter === 'all' || log.user_id === userFilter;
    
    return matchesSearch && matchesAction && matchesUser;
  });

  const uniqueActions = [...new Set(logs.map(l => l.action_type))].filter(Boolean);
  
  // Get user name - check fetched users first, then fallback to truncated ID
  const getUserName = (userId) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.user_id === userId);
    return user?.full_name || userId.slice(0, 15) + '...';
  };

  return (
    <div data-testid="audit-log-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audit Log</h1>
          <p className="text-slate-500">Track all system activities and changes</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Clock className="w-4 h-4" />
          <span>Showing last {limit} entries</span>
        </div>
      </div>

      <DownloadRequestsPanel />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-base pl-10"
            data-testid="search-logs"
          />
        </div>
        <select
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          className="input-base w-full sm:w-48"
          data-testid="user-filter"
        >
          <option value="all">All Users</option>
          {displayUsers.map(user => (
            <option key={user.user_id} value={user.user_id}>{user.full_name}</option>
          ))}
        </select>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="input-base w-full sm:w-48"
        >
          <option value="all">All Actions</option>
          {uniqueActions.map(action => (
            <option key={action} value={action}>{formatAction(action)}</option>
          ))}
        </select>
        <select
          value={limit}
          onChange={(e) => setLimit(parseInt(e.target.value))}
          className="input-base w-full sm:w-32"
        >
          <option value="50">50 entries</option>
          <option value="100">100 entries</option>
          <option value="200">200 entries</option>
          <option value="500">500 entries</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-slate-900">{logs.length}</p>
              <p className="text-xs text-slate-500">Total Entries</p>
            </div>
            <Activity className="w-8 h-8 text-indigo-200" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-green-600">
                {logs.filter(l => l.action_type === 'create' || l.action_type?.includes('create')).length}
              </p>
              <p className="text-xs text-slate-500">Creates</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-200" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {logs.filter(l => l.action_type === 'update' || l.action_type?.includes('update')).length}
              </p>
              <p className="text-xs text-slate-500">Updates</p>
            </div>
            <Edit2 className="w-8 h-8 text-blue-200" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-amber-600">
                {logs.filter(l => l.action_type === 'suspend' || l.action_type?.includes('reject')).length}
              </p>
              <p className="text-xs text-slate-500">Actions</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-amber-200" />
          </div>
        </div>
      </div>

      {/* Log Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="p-4 text-left">Timestamp</th>
                <th className="p-4 text-left">Action</th>
                <th className="p-4 text-left">Entity</th>
                <th className="p-4 text-left">User</th>
                <th className="p-4 text-left">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log, idx) => (
                  <tr key={log.activity_id || idx} className="table-row">
                    <td className="table-cell">
                      <div className="text-sm text-slate-900">
                        {log.timestamp ? new Date(log.timestamp).toLocaleDateString() : '-'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ''}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        {getActionIcon(log.action_type)}
                        <span className="font-medium text-slate-900">
                          {formatAction(log.action_type)}
                        </span>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        {getEntityIcon(log.entity_type)}
                        <div>
                          <div className="text-sm text-slate-900 capitalize">{log.entity_type}</div>
                          <div className="text-xs text-slate-500 font-mono">{log.entity_id?.slice(0, 20)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-slate-900">
                        {getUserName(log.user_id)}
                      </div>
                    </td>
                    <td className="table-cell">
                      {log.notes ? (
                        <span className="text-sm text-slate-600">{log.notes}</span>
                      ) : log.old_value || log.new_value ? (
                        <button
                          className="text-sm text-indigo-600 hover:underline"
                          onClick={() => {
                            const details = `Old: ${JSON.stringify(log.old_value, null, 2)}\n\nNew: ${JSON.stringify(log.new_value, null, 2)}`;
                            alert(details);
                          }}
                        >
                          View changes
                        </button>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditLogPage;
